import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export const SHOW_ALTERNATIVES_STORAGE_KEY = 'memrise_show_alternatives_by_language';

type ShowAlternativesByLanguage = Record<string, boolean>;

const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach(listener => listener());
};

const normalizeLanguage = (language: string): string => language.trim().toUpperCase();

const readStoredPreferences = (): ShowAlternativesByLanguage => {
  const raw = getLocalStorageEntry(SHOW_ALTERNATIVES_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ShowAlternativesByLanguage) : {};
  } catch {
    return {};
  }
};

export const getShowAlternatives = (language: string): boolean => {
  /* istanbul ignore next: SSR guard */
  if (typeof window === 'undefined') return false;

  return readStoredPreferences()[normalizeLanguage(language)] === true;
};

export const setShowAlternatives = (language: string, shown: boolean): void => {
  /* istanbul ignore next: SSR guard */
  if (typeof window === 'undefined') return;

  const preferences = readStoredPreferences();
  preferences[normalizeLanguage(language)] = shown;
  setLocalStorageEntry(SHOW_ALTERNATIVES_STORAGE_KEY, JSON.stringify(preferences));
  notify();
};

export const toggleShowAlternatives = (language: string): void => {
  setShowAlternatives(language, !getShowAlternatives(language));
};

export const subscribeShowAlternatives = (listener: () => void): (() => void) => {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    if (event.key === SHOW_ALTERNATIVES_STORAGE_KEY || event.key === null) {
      listener();
    }
  };

  /* istanbul ignore else: SSR guard */
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return () => {
    listeners.delete(listener);
    /* istanbul ignore else: SSR guard */
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
};
